import {AnimatePresence, motion} from 'framer-motion'
import {useEffect, useMemo, useState} from 'react'
import {twJoin} from 'tailwind-merge'
import {nanoid} from 'nanoid'

import {noop} from '@sdox/utils'

import {EnterExitVariants, usePrevious} from '../../'

import type {ClassNameValue} from 'tailwind-merge'
import type {ReactNode} from 'react'
import type {DragHandlers} from 'framer-motion'

interface CarouselProps {
  itemClassName?: ClassNameValue
  index: number
  showPrevious: () => void
  showNext: () => void
  shouldHandleArrowKeys?: boolean
  withSwipeHandling?: boolean
  renderItem: (index: number) => ReactNode
}

const swipeConfidenceThreshold = 10
const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity

export const GenericCarousel = ({
  itemClassName,
  index,
  shouldHandleArrowKeys = false,
  withSwipeHandling = true,
  showPrevious,
  showNext,
  renderItem,
}: CarouselProps) => {
  const [isGrabbing, setIsGrabbing] = useState(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // we need to generate a new key whenever the index changes or there will be animation bugs when
  // quickly switching back and forth
  const key = useMemo(() => nanoid(), [index])
  const previousIndex = usePrevious<number>(index)
  const hasReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const direction = useMemo(() => {
    if (index > previousIndex) return 1
    if (index < previousIndex) return -1
    return 0
  }, [index, previousIndex])

  useEffect(() => {
    if (!shouldHandleArrowKeys) return

    const handleArrowKeys = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') showNext()
      if (event.key === 'ArrowLeft') showPrevious()
    }

    document.addEventListener('keydown', handleArrowKeys)
    return () => {
      document.removeEventListener('keydown', handleArrowKeys)
    }
  }, [shouldHandleArrowKeys, showPrevious, showNext])

  const handleSwipe: DragHandlers['onDragEnd'] = (e, {offset, velocity}) => {
    const swipe = swipePower(offset.x, velocity.x)

    if (swipe < -swipeConfidenceThreshold) {
      showNext()
    } else if (swipe > swipeConfidenceThreshold) {
      showPrevious()
    }
  }

  return (
    <AnimatePresence
      initial={false}
      mode={'popLayout'}
      custom={direction}
    >
      <motion.div
        key={key}
        custom={direction}
        className={twJoin(
          itemClassName,
          isGrabbing ? 'cursor-grabbing' : 'cursor-grab',
        )}
        variants={EnterExitVariants({
          none: '0',
          right: '100%',
          left: '-100%',
        })}
        initial={'enter'}
        animate={'center'}
        exit={'exit'}
        transition={{
          x: hasReducedMotion ? {duration: 0} : {type: 'spring', stiffness: 300, damping: 30},
          opacity: {duration: hasReducedMotion ? 0 : 0.3},
        }}
        drag={'x'}
        dragConstraints={{left: 0, right: 0}}
        dragElastic={1}
        onMouseDown={() => setIsGrabbing(true)}
        onMouseUp={() => setIsGrabbing(false)}
        onDragEnd={(e, info) => withSwipeHandling ? handleSwipe(e, info) : noop}
      >
        {renderItem(index)}
      </motion.div>
    </AnimatePresence>
  )
}
